import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Link from 'next/link';
import { Progress } from './Progress';
import { Spacer } from '../Layout/Spacer';
import dynamic from 'next/dynamic';
import { useMediaQuery } from 'react-responsive';
import { Consts as StyleConsts } from '../../styles/consts';

const Video = dynamic(() => import('../LessonContent/Video'), { ssr: false });

interface ICoursesByNameItem {
  previewImage: {
    filename: string;
    alt: string;
  };
  previewTitle: string;
  fullSlug: string;
  percentComplete?: number;
  learningContent?: boolean;
  description?: string;
  video?: {
    url: {
      url?: string;
      cached_url?: string;
    };
    video: {
      filename?: string;
    };
    autoplay: boolean;
    controls: boolean;
    loop: boolean;
    muted: boolean;
    cover?: {
      filename: string;
    };
    cover_url?: {
      url: string;
      cached_url: string;
    };
  };
  setShow?: any;
  setFullSlug?: any;
  setTitle?: any;
  setDescription?: any;
  setVideo?: any;
  setHeight?: any;
}

import { scrollTop } from 'utils';

export const CoursesByNameItem: FunctionComponent<ICoursesByNameItem> = ({
  previewImage,
  previewTitle,
  fullSlug,
  percentComplete,
  learningContent,
  description,
  video,
  setShow,
  setFullSlug,
  setTitle,
  setDescription,
  setVideo,
  setHeight,
}) => {
  const [isMobile, setMobile] = useState(false);
  const mobile = useMediaQuery({
    query: `(max-width: ${StyleConsts.mobileBreakPoint})`,
  });

  useEffect(() => {
    setMobile(mobile);
  }, [mobile]);
  const [isHover, setIsHover] = useState(false);
  const [right, setRight] = useState(false);
  const [left, setLeft] = useState(false);

  const sliderRef = useRef<HTMLDivElement>(null);
  const coordLeft =
    sliderRef && isHover && sliderRef.current.getBoundingClientRect().x;
  const slideClassName =
    sliderRef &&
    isHover &&
    sliderRef.current.parentElement.parentElement.className;
  const width =
    sliderRef && isHover && sliderRef.current.getBoundingClientRect().width;
  const [isTablet, setTablet] = useState(false);
  const tablet = useMediaQuery({
    query: `(max-width: 1152px)`,
  });

  useEffect(() => {
    setTablet(tablet);
  }, [tablet]);

  useEffect(() => {
    setRight(
      slideClassName === 'slick-slide slick-active' &&
        document.documentElement.clientWidth -
          (coordLeft + width + (isTablet ? 166 : 165)) <
          170,
    );
  }, [sliderRef, isHover]);
  useEffect(() => {
    setLeft(
      slideClassName === 'slick-slide slick-active slick-current' &&
        coordLeft - 20 === 147,
    );
  }, [sliderRef, isHover]);

  const openPopUp = () => {
    setHeight(window.document.documentElement.scrollTop);
    (
      document.getElementsByTagName('HTML') as HTMLCollectionOf<HTMLElement>
    )[0].style.scrollBehavior = 'auto';
    setShow(true);
    window.scrollTo({ top: 0, behavior: 'auto' });
    setFullSlug(fullSlug);
    setTitle(previewTitle);
    setDescription(description);
    setVideo(video);

    const layout = document.getElementById('layout') as HTMLDivElement;
    layout.style.overflowY = 'hidden';
    layout.style.height = '100vh';
    layout.style.maxHeight = '100vh';
  };

  return (
    <>
      {learningContent ? (
        <ItemWrapper
          learningContent={learningContent}
          ref={sliderRef}
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
        >
          {!isMobile && (
            <Overlay
              right={right}
              left={left}
              learningContent={learningContent}
            >
              <Video
                blok={video}
                learningContent={learningContent}
                link={fullSlug}
                isShow={isHover}
              />
              <TextWrapper learningContent={learningContent}>
                <OverlayTitle>{previewTitle}</OverlayTitle>
                <OverlayText learningContent={learningContent}>
                  {description}
                </OverlayText>
              </TextWrapper>
            </Overlay>
          )}
          <ImageWrapper onClick={() => (isMobile ? openPopUp() : {})}>
            <img src={previewImage.filename} alt={previewImage.alt} />
          </ImageWrapper>
          <Spacer height={16} />
          <Title>{previewTitle}</Title>
        </ItemWrapper>
      ) : (
        <ItemWrapper>
          {!isMobile ? (
            <>
              <Overlay>
                <Link href={`/${fullSlug}`}>
                  <ImageWrapper>
                    <OverlayBackground>
                      <Text>Watch the video</Text>
                      <img src="/images/arrow-play.svg" alt="" />
                    </OverlayBackground>
                    <img src={previewImage.filename} alt={previewImage.alt} />
                  </ImageWrapper>
                </Link>
                <TextWrapper>
                  <OverlayTitle>{previewTitle}</OverlayTitle>
                  <OverlayText>{description}</OverlayText>
                </TextWrapper>
              </Overlay>
              <ImageWrapper>
                <img src={previewImage.filename} alt={previewImage.alt} />
              </ImageWrapper>
            </>
          ) : (
            <Link href={`/${fullSlug}`}>
              <ImageWrapper>
                <OverlayBackground>
                  <Text>Watch the video</Text>
                  <img src="/images/arrow-play.svg" alt="" />
                </OverlayBackground>
                <img src={previewImage.filename} alt={previewImage.alt} />
              </ImageWrapper>
            </Link>
          )}
          <Spacer height={16} />
          {Number(percentComplete) > 0 && Number(percentComplete) < 100 && (
            <>
              <Progress main={false} percentComplete={percentComplete} />
              <Spacer height={8} />
            </>
          )}
          <Title>{previewTitle}</Title>
        </ItemWrapper>
      )}
    </>
  );
};
interface ItemWrapper {
  learningContent?: boolean;
}

const ItemWrapper = styled.div<ItemWrapper>`
  ${({ learningContent, theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
    flex-basis: 25%;
    padding: 0 8px;

    position: relative;

    &:hover > div:first-of-type {
      transition: all ease 0.4s;
      opacity: 1;
      transform: ${learningContent
        ? 'translateX(-16.73%) scale(1)'
        : 'translateX(-7%) scale(1)'};
    }
  `}
`;

interface IOverlay {
  left?: boolean;
  right?: boolean;
  learningContent?: boolean;
}

const Overlay = styled.div<IOverlay>`
  ${({ learningContent, left, right, theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
    position: absolute;
    width: ${learningContent ? '149.46%' : '110%'};
    z-index: 20;
    top: ${learningContent ? '-23px' : '-25px'};
    overflow: visible;
    left: ${left && '25%'};
    right: ${right && '-25%'};
    transform: ${learningContent
      ? 'translateX(-16.73%) scale(0)'
      : 'translateX(-7%) scale(0)'};
    background: #222222;
    opacity: 0;
    border-radius: 8px;
  `}
`;
const TextWrapper = styled.div<ItemWrapper>`
  ${({ learningContent, theme: { Consts } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${learningContent ? '12px' : '8px'};
    padding: ${learningContent ? '30px 24px' : '12px'};
  `}
`;
const OverlayTitle = styled.h4`
  ${({ theme: { Consts } }) => css`
    font-weight: 700;
    font-size: 17px;
    line-height: 22px;
    letter-spacing: 0.01em;
  `}
`;
const OverlayText = styled.p<ItemWrapper>`
  ${({ learningContent, theme: { Consts } }) => css`
    font-weight: 400;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0.01em;
    color: #ebf5f3;
    padding-right: 12px;
    max-height: ${learningContent ? '233px' : '110px'};
    overflow-y: scroll;

    &::-webkit-scrollbar {
      width: 2px;
    }

    &::-webkit-scrollbar-thumb {
      background: #ebf5f3;
      height: 35px;
    }
  `}
`;
const Title = styled.h2`
  ${({ theme: { Consts } }) => css`
    font-weight: 300;
    font-size: 17px;
    line-height: 130%;
    color: #ffffff;
  `}
`;

const ImageWrapper = styled.a`
  ${({ theme: { Consts } }) => css`
    display: block;
    cursor: pointer;
    position: relative;
    border-radius: 4px;

    &:hover div {
      display: flex;
    }

    img {
      border-radius: 4px;
      width: 100%;
    }
  `}
`;
const OverlayBackground = styled.div`
  ${({ theme: { Consts } }) => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: none;
    justify-content: center;
    align-items: center;
    gap: 12px;
    border: 1px solid #ffffff;
    border-radius: 4px;

    img {
      width: 32px !important;
      height: 32px;
    }
  `}
`;
const Text = styled.p`
  ${({ theme: { Consts } }) => css`
    font-weight: 300;
    font-size: 16px;
    line-height: 130%;
    color: #ffffff;
  `}
`;
